/* eslint-disable no-param-reassign */
import _ from 'underscore';
import Promise from 'bluebird';
import $ from 'jquery';

import { AzureUploader } from '@bingads-webui/azure-upload';
import { ODataAzureUploader } from '@bingads-webui/odata-azure-upload';
import * as urlUtil from '@bingads-webui/url-util';
// TODO: Fix exports for cookie, safeJson shared components
import { get, set } from '@bingads-webui/cookie';
import { http } from '@bingads-webui/http-util';
import { parseSync, stringifySync } from '@bingads-webui/safe-json';

import { generateAllowedEntities } from './entities-name-localizer';
import { UIBlockerWithStepsView } from './blocker/index';
import { defaultOptions } from './default-options';

const COOKIE_PATH = '/';
const IMPORT_COOKIE_NAME = 'ImportCookie';

const STATUS_NOTSTARTED = 1;
const STATUS_UPLOAD = 2;
const STATUS_VALIDATIONINPROGRESS = 3;
const STATUS_VALIDATIONCOMPLETED = 4;
const STATUS_SUBMITINPROGRESS = 5;
const STATUS_COMPLETE = 6;
const STATUS_CANCELLED = 7;
const STATUS_FAILED = 8;
// Status, not ImportStatus
const TASK_ITEM_EXECUTION_STATUS_QUEUED = 'PendingExecution';

const statusEnumValueToKeyMap = [];
statusEnumValueToKeyMap[STATUS_NOTSTARTED] = 'NotStarted';
statusEnumValueToKeyMap[STATUS_UPLOAD] = 'UploadInProgress';
statusEnumValueToKeyMap[STATUS_VALIDATIONINPROGRESS] = 'ValidationInProgress';
statusEnumValueToKeyMap[STATUS_VALIDATIONCOMPLETED] = 'ValidationCompleted';
statusEnumValueToKeyMap[STATUS_SUBMITINPROGRESS] = 'SubmitInProgress';
statusEnumValueToKeyMap[STATUS_COMPLETE] = 'Complete';
statusEnumValueToKeyMap[STATUS_CANCELLED] = 'Cancelled';
statusEnumValueToKeyMap[STATUS_FAILED] = 'Failed';

const OpportunityApplyStatus = {
  Opportunity_NotStart: 'NotStart',
  Opportunity_InProgress: 'InProgress',
  Opportunity_Succeed: 'Succeed',
  Opportunity_Failed: 'Failed',
};

const importErrorBulkFileType = 'ImportErrorFile';

const getImportStatusWithIdUrlTmpl =
  '/ImportSession/ImportStatus(CustomerId=:customer_id,AccountId=:account_id)';
const getImportSummaryUrlTmpl =
  '/ImportSession/ImportSubmittedCounts(CustomerId=:customer_id,AccountId=:account_id)';
const createTaskExecutionUrlTmpl = '/Customers()/Accounts()/TaskItems?%24expand=TaskItemExecutions';
const pollTaskExecutionUrlTmpl = '/Customers()/Accounts()/TaskItemExecutions({{taskItemExecutionId}})';
const updateImportStatusUrlTmpl =
  '/ImportSession/UpdateImportStatus(CustomerId=:customer_id,AccountId=:account_id)';
const exportErrorFileUrlTmpl =
  '/ImportSession/ExportImportedData(CustomerId=:customer_id,AccountId=:account_id)';
const importUserPreferencesUrlTmpl =
  '/ImportSession/ImportUserPreferences(CustomerId=:customer_id,AccountId=:account_id)';
const importUserPreferenceUrlTmpl =
  '/ImportSession/ImportUserPreference(CustomerId=:customer_id,AccountId=:account_id)';
const defaultImportUserPreferenceUrlTmpl =
  '/ImportSession/DefaultImportUserPreference(CustomerId=:customer_id,AccountId=:account_id)';
const importMultiAccountUserPreferenceUrlTmpl =
  '/ImportSession/MultiAccountGoogleImportUserPreference(CustomerId=:customer_id)';
const getBulkFileUrlTmpl = '/BulkUploadFiles/GetBulkFileUrl(AccountId=:account_id,fileIdentifier={{blobName}},BulkFileType={{bulkFileType}})';
const campaignUrl = '/Campaign/Campaigns';
const fileImportUrl = '/Campaign/ImportCampaign/FileImport';
const newGridLastImportFilter = {
  UpdatedOnLastImport: {
    $eq: true,
  },
};

export const getPendingImportStatus = () => new Promise(resolve => resolve(null));

export class CommonImportRepository {
  constructor({
    deps,
    type = '',
    importType,
  }) {
    this.deps = deps;
    this.type = type;
    this.importType = importType;
    this.i18n = deps.i18n;
    this.i18nErrors = deps.i18nErrors;
    this.adGroupMedium = deps.adGroupMedium;
    this.storeList = deps.storeList;
    this.config = deps.config;
    this.permissions = this.config.get('Permissions') || {};
    this.allowedList = generateAllowedEntities({
      i18n: this.i18n,
      permissions: this.permissions,
      allowedChannels: ['GoogleImport', 'FacebookImport', 'AmazonImport', 'PinterestImport'],
      importType: type,
      overrideEntitiesGenerator: deps.overrideEntitiesGenerator,
    });
    this.language = deps.language;
    this.market = deps.market;
    this.odata = deps.odata;
    this.paymentMethodUrl = deps.paymentMethodUrl;
    this.statusKeyToOrderMap = deps.statusKeyToOrderMap;
    this.statusOrderToBlockerTitleMap = deps.statusOrderToBlockerTitleMap;
    this.timeZoneEnumMapper = deps.timeZoneEnumMapper;
    this.aid = this.config.get('CurrentAccount').Id;
    this.cid = this.config.get('CurrentCustomer').Id;
    this.uid = this.config.get('CurrentUser').Id;
    this.advCustomerId = this.config.get('CurrentAccount').AdvertiserCustomerID;
    this.sharedCookieDomain = deps.sharedCookieDomain;
    this.coupon = deps.coupon && deps.coupon.rawData;
    this.calloutcardCoupon = deps.calloutcardCoupon;
    this.ccJsData = deps.ccJsData;
    // this is used for MT testing purpose. Only enabled on tip.
    // when true, type field is set with 'Test' suffix when creating task item execution.
    this.testTypeSuffix = this.permissions.IsUseImportTestTypeForImportTaskExecutionEnabled ? 'Test' : '';
    this.opportunityApplyStatus = OpportunityApplyStatus.Opportunity_NotStart;
    this.localImportCookie = null;
  }

  getImportCookieFromLocal = () => this.localImportCookie;

  setImportCookieToLocal = (cookieJson) => {
    this.localImportCookie = cookieJson;
  };

  cookieKeyByAccountId() {
    return `${this.aid}_aid`;
  }

  filterLocalizedEntities(data) {
    const dashVal = this.i18n.get('Import_LastImport_Dash');
    const filtered = _.chain(this.allowedList)
      .filter(value => data[value.MTName])
      .map((value) => {
        const entityData = data[value.MTName];
        const original = {
          UINameKey: value.UINameKey,
          Name: value.UIName,
          Additions: entityData.Additions,
          Changes: entityData.Changes,
          Deletions: entityData.Deletions,
          Errors: entityData.Errors,
          Total: entityData.Total,
          HasWarning: entityData.HasWarning,
        };

        // for null/undefined value, display '-'
        _.each(original, (val, key) => {
          if (_.isNull(val) || _.isUndefined(val)) {
            // Override HasWarning to false if its value is undefined or null
            original[key] = (key === 'HasWarning') ? false : dashVal;
          }
        });

        return original;
      })
      .value();

    return filtered;
  }

  getImportStatus() {
    // pass import id 0 for fetching last import
    const url = this.formatUrl(
      {
        url: getImportStatusWithIdUrlTmpl,
        queryParameters: {
          importId: 0,
        },
      },
      false
    );

    return this.odata.get(url)
      .then((status) => {
        const mappedStatus = _.clone(status);

        mappedStatus.CurrentImportStatus = statusEnumValueToKeyMap[status.CurrentImportStatus];
        return mappedStatus;
      });
  }

  isAnyImportPending() {
    return getPendingImportStatus(this.odata);
  }

  isImportStatusValid(status) {
    return status && status.ImportId !== 0;
  }

  redirectToInProgressImport(url, importId) {
    const redirectUrl = this.formatUrl({
      url,
      queryParameters: {
        importId,
      },
    }, true);

    this.navigateToUrl(redirectUrl);
  }

  redirectToInProgressFileImport(importId) {
    if (this.deps.isForVNext) {
      this.deps.router.navigateToRoute('fileimport', { importId });
    } else {
      this.redirectToInProgressImport(fileImportUrl, importId);
    }
  }

  toTargetImportStatusStep(importId, routePath, toImportStatusStep = _.noop) {
    const { pathname } = window.location;
    if (pathname.search(`/${routePath}`) !== -1) {
      toImportStatusStep();
    } else {
      this.deps.router.navigateToRoute(routePath, { importId });
    }
  }

  redirectToInProgressGoogleImport(importId, toImportStatusStep) {
    if (this.deps.isForVNext) {
      this.toTargetImportStatusStep(importId, 'googleimport', toImportStatusStep);
    } else {
      // ad hoc fix for MAE
      const googleImportUrl = '/Campaign/ImportCampaign/GoogleImport';
      this.redirectToInProgressImport(googleImportUrl, importId);
    }
  }

  redirectToInProgressFacebookImport(importId, toImportStatusStep) {
    this.toTargetImportStatusStep(importId, 'facebookimport', toImportStatusStep);
  }

  // options: { url, routerValue, queryParameters }
  formatUrl(options, addDefaultParameters = false) {
    let url;
    let queryParameters;
    let routerValue;

    if (_.isString(options)) {
      url = options;
    } else {
      ({ url } = options);
      ({ queryParameters } = options);
      ({ routerValue } = options);
    }
    if (addDefaultParameters) {
      queryParameters = _.extend({}, queryParameters, {
        aid: this.aid,
        cid: this.cid,
        uid: this.uid,
      });
    }

    if (!_.isEmpty(queryParameters)) {
      url = urlUtil.queryify(url, queryParameters);
    }

    if (routerValue) {
      url += `#customer/${this.cid}/account/${this.aid}/${routerValue}`;
    }

    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }

    return url;
  }

  navigateToUrl(url) {
    window.location.href = url;
  }

  redirectToAddPayment() {
    const url = this.formatUrl({
      url: this.paymentMethodUrl,
    }, true);

    this.navigateToUrl(url);
  }

  redirectToImportedCampaigns(hasSuccessResult) {
    if (_.isFunction(this.deps.redirectToImportedCampaigns)) {
      this.deps.redirectToImportedCampaigns();
    } else {
      this.deps.campaignFilterStore.save({ query: newGridLastImportFilter });
      this.deps.adgroupFilterStore.save({ query: newGridLastImportFilter });
      this.deps.adFilterStore.save({ query: newGridLastImportFilter });

      if (this.deps.isForVNext) {
        if (hasSuccessResult) {
          this.deps.CampaignCacheManager.invalidate('Campaign');
        }
        this.deps.router.navigateToRoute('campaign');
      } else {
        const url = this.formatUrl({
          url: campaignUrl,
          queryParameters: {
            filterLastImport: true,
          },
          routerValue: 'campaign',
        }, true);
        this.navigateToUrl(url);
      }
    }
  }

  redirectToCampaigns() {
    if (this.deps.isForVNext) {
      this.deps.router.navigateToRoute('campaign');
    } else {
      this.navigateToUrl(this.formatUrl({
        url: campaignUrl,
        routerValue: 'campaign',
      }, true));
    }
  }

  redirectToImportScheduleAndHistory() {
    if (this.deps.isForVNext) {
      if (this.type) {
        this.deps.router.navigateToRoute('importschedule', { importType: this.type.toLowerCase() });
      } else {
        this.deps.router.navigateToRoute('importschedule');
      }
    } else {
      const url = this.formatUrl({
        url: campaignUrl,
        routerValue: 'importschedule',
      }, true);

      this.navigateToUrl(url);
    }
  }

  redirectToMetaImport(options) {
    if (options) {
      this.deps.router.navigateToRoute('facebookimport', options);
    } else {
      this.deps.router.navigateToRoute('facebookimport');
    }
  }

  cancelCurrentImport(importId) {
    const url = this.formatUrl({
      url: updateImportStatusUrlTmpl,
      queryParameters: {
        importId,
        status: STATUS_CANCELLED,
      },
    }, false);

    return this.odata.post(url);
  }

  cancelCurrentImportAndRedirectToCampaigns(importId) {
    return this.cancelCurrentImport(importId)
      .then(() => {
        this.redirectToCampaigns();
      });
  }

  getImportSummary(importId, collapseTargetsData = true) {
    const url = this.formatUrl({
      url: getImportSummaryUrlTmpl,
      queryParameters: {
        importId,
      },
    });

    return this.odata.get(url)
      .then((data) => {
        const filteredData = this.filterLocalizedEntities(data);

        // group target
        const result = [];
        const targetsAggregated = {
          Name: this.i18n.get('CampaignImport_ImportSummaryTargets'),
          Expandable: true,
          Children: [],
          Additions: 0,
          Changes: 0,
          Errors: 0,
          Deletions: 0,
        };

        _.each(filteredData, (entity) => {
          // if UINameKey ends with 'Targets', we group it into targets category.
          if (/Targets$/.test(entity.UINameKey) && collapseTargetsData) {
            targetsAggregated.Children.push(_.extend({}, entity, { IsChildren: true }));
            targetsAggregated.Additions += entity.Additions;
            targetsAggregated.Changes += entity.Changes;
            targetsAggregated.Errors += entity.Errors;
            targetsAggregated.Deletions += entity.Deletions;
          } else {
            result.push(entity);
          }
        });

        if (!_.isEmpty(targetsAggregated.Children)) {
          result.push(targetsAggregated);
        }

        return result;
      });
  }

  getBulkBlob(blobName, bulkFileType) {
    const odataUrl = getBulkFileUrlTmpl.replace('{{blobName}}', blobName)
      .replace('{{bulkFileType}}', bulkFileType);
    return this.odata.get(odataUrl)
      .then(blobUrl => http.get(blobUrl))
      // return empty {} if blob is undefined.
      .then(blob => blob || {});
  }

  uploadToAzureBlobViaOdata({ bulkFileType, payload, logger }) {
    // record the null/undefined payload for we have 1 error with empty file content.
    const payloadStatus = payload ? '' : `payload=${JSON.stringify(payload)} `;
    const uploadLogger = logger || {
      trace: (msg, api, requestId) => this.logTrace(`${payloadStatus}API=${api} RID=${requestId} msg=${msg}`),
      error: (msg, api, requestId) => this.logError(`${payloadStatus}API=${api} RID=${requestId} msg=${msg}`),
    };
    const odataUploader = new ODataAzureUploader({
      odata: this.odata,
      azureUploader: new AzureUploader({
        logger: uploadLogger,
      }),
      logger: uploadLogger,
    });

    return odataUploader.createBlobWithODataAndUpload({
      bulkFileType,
      payload,
      useAccountId: true,
    });
  }

  prepareBaseImportTaskItem() {
    const { config } = this;

    return {
      '@odata.type': '#Model.ImportTaskItem',
      Type: `Import${this.testTypeSuffix}`,
      AccountId: config.get('CurrentAccount') && config.get('CurrentAccount').Id,
      CultureName: config.get('CultureName'),
    };
  }

  prepareCompleteImportTaskItem(importUserPreferences, timeZoneId) {
    const { config } = this;
    const { ShortName, Name, AccountNumber } = (config && config.get('CurrentAccount')) || {};
    let taskItem = _.extend(this.prepareBaseImportTaskItem(), {
      CampaignTimeZone: this.timeZoneEnumMapper.getEnumbyId(timeZoneId || config.get('AccountTimeZone')),
      LCID: config.get('CultureLcid'),
      AdGroupMedium: this.adGroupMedium,
      Language: this.language,
      CustomerMarket: this.market,
      UserName: config.get('CurrentUser') && config.get('CurrentUser').Name,
      AccountName: ShortName || Name,
      AccountNumber,
      NotificationMedium: 'Email',
      TimeZoneId: timeZoneId,
    });
    taskItem = _.extend(
      taskItem,
      {
        ImportUserPreference: importUserPreferences,
      }
    );
    return taskItem;
  }

  createRunNowImportTaskItem(data) {
    return this.jsData.TaskItem.create(data, _.extend(this.taskItemOptions, {
      urlParameters: {
        $expand: 'TaskItemExecutions',
      },
    }));
  }

  createScheduledImportTaskItem(data) {
    return this.jsData.TaskItem.create(data, _.extend(this.taskItemOptions, {
      urlParameters: {
        $expand: 'TaskItemExecutions',
      },
    }));
  }

  updateScheduledImportTaskItem(data, taskItemId) {
    return this.jsData.TaskItem.update(taskItemId, data, _.extend(this.taskItemOptions, {
      urlParameters: {
        $expand: 'TaskItemExecutions',
      },
    }));
  }

  createImportTaskItemExecution(data) {
    return this.odata.post(createTaskExecutionUrlTmpl, { data })
      .then((result) => {
        const execution = _.first(result.TaskItemExecutions);

        return execution && execution.Id;
      });
  }

  shouldStopPolling(data) {
    const importComplete = data.ImportStatus === 'Complete' || data.ImportStatus === 'Failed' ||
      data.ImportStatus === 'Cancelled' || data.Status === 'Failed' || !!data.ErrorCode;

    return data.Status !== TASK_ITEM_EXECUTION_STATUS_QUEUED && importComplete;
  }

  isValidCustomBlockerStatus(data) {
    return (_.has(this.statusKeyToOrderMap, data.ImportStatus)
        || _.has(this.statusKeyToOrderMap, data.Status));
  }

  checkAndRenderCustomBlocker() {
    if (!this.hasCustomizedBlocker) {
      $('.blocker .blocker-text').html(this.blockerView.render().el);
      this.hasCustomizedBlocker = true;
    }
  }

  updateCustomBlockerStatus(data) {
    if (data.Status === TASK_ITEM_EXECUTION_STATUS_QUEUED) {
      this.blockerView.update({
        currentStatusOrder: this.statusKeyToOrderMap[data.Status],
        importId: 0,
      });
    } else {
      this.blockerView.update({
        currentStatusOrder: this.statusKeyToOrderMap[data.ImportStatus],
        importId: data.ImportId,
      });
    }
  }

  // Used for continue import scenarios, where most recent task item execution is not
  // necessarily most recent or currently running import
  pollGetImportStatus(importId, closeBlock) {
    const url = this.formatUrl(
      {
        url: getImportStatusWithIdUrlTmpl,
        queryParameters: {
          importId,
        },
      },
      false
    );

    return this.pollImportStatus(
      url,
      this.mapGetImportStatusResponseToTaskItemExecution.bind(this),
      closeBlock
    );
  }

  mapGetImportStatusResponseToTaskItemExecution(data) {
    return {
      ImportStatus: statusEnumValueToKeyMap[data.CurrentImportStatus],
      ErrorCode: data.FailureReason,
      ImportId: data.ImportId,
    };
  }

  pollTaskItemExecutionStatus(taskItemExecutionId, closeBlock) {
    const url = pollTaskExecutionUrlTmpl.replace('{{taskItemExecutionId}}', taskItemExecutionId);

    return this.pollImportStatus(url, taskItemExecutionId, closeBlock);
  }

  pollSmartWorkflowImportStatus(url, importStatusContainer, importStatusMapper) {
    return new Promise((resolve, reject) => {
      let failedAttempts = 0;
      let lastCallCompleted = true;
      const MAX_FAILED_ATTEMPTS = 5;

      function onStatusCheckSuccess(data) {
        // update the import status panel once poll import status succeeded
        if (this.isValidCustomBlockerStatus(data)) {
          importStatusContainer.show();
          if (data.Status === TASK_ITEM_EXECUTION_STATUS_QUEUED) {
            importStatusContainer.update(this.statusKeyToOrderMap[data.Status]);
          } else {
            importStatusContainer.update(this.statusKeyToOrderMap[data.ImportStatus]);
          }
        }

        if (this.shouldStopPolling(data)) {
          clearInterval(this.timeOut);
          if (data.ImportStatus === 'Failed' || data.Status === 'Failed' ||
            data.ErrorCode || data.ImportStatus === 'Cancelled') {
            const err = new Error();

            err.customized = {
              errorCode: data.ErrorCode,
              message: this.getLocalizedErrorMessage(data.ErrorCode, true),
              errorDetail: data.ErrorDetail,
              isCancelled: data.ImportStatus === 'Cancelled',
            };
            reject(err);
          } else {
            importStatusContainer.goToImportSummaryPage(data);
            resolve({
              importId: data.ImportId,
              isBatch: data.CampaignSplitBatchIndex > 0 && data.CampaignSplitBatchCount > 0,
            });
          }
        }
      }

      function checkStatus() {
        if (lastCallCompleted) {
          lastCallCompleted = false;
          this.odata.get(url)
            .then((rawData) => {
              let data = rawData;
              if (_.isFunction(importStatusMapper)) {
                data = importStatusMapper(rawData);
              }
              onStatusCheckSuccess.call(this, data);
            })
            .catch((err) => {
              failedAttempts += 1;
              if (failedAttempts === MAX_FAILED_ATTEMPTS) {
                clearInterval(this.timeOut);
                reject(err);
              }
            })
            .finally(() => {
              lastCallCompleted = true;
            });
        }
      }
      this.timeOut = setInterval(checkStatus.bind(this), 1000);
    });
  }

  pollImportStatus(url, dataMapper, closeBlock) {
    return new Promise((resolve, reject) => {
      const MAX_FAILED_ATTEMPTS = 5;
      let failedAttempts = 0;
      let lastCallCompleted = true;
      this.blockerView = new UIBlockerWithStepsView({
        steps: this.statusOrderToBlockerTitleMap,
        i18n: this.i18n,
        repository: this,
        closeBlock,
      });
      this.hasCustomizedBlocker = false;

      function onStatusCheckSuccess(data) {
        failedAttempts = 0;
        // we will update to the customized blocker once import is queued for execution
        if (this.isValidCustomBlockerStatus(data)) {
          this.checkAndRenderCustomBlocker();
          this.updateCustomBlockerStatus(data);
        }

        if (this.shouldStopPolling(data)) {
          clearInterval(this.timeOut);
          if (data.ImportStatus === 'Failed' || data.Status === 'Failed' ||
            data.ErrorCode || data.ImportStatus === 'Cancelled') {
            const err = new Error();

            err.customized = {
              errorCode: data.ErrorCode,
              message: this.getLocalizedErrorMessage(data.ErrorCode, true),
              errorDetail: data.ErrorDetail,
              isCancelled: data.ImportStatus === 'Cancelled',
            };
            reject(err);
          } else {
            resolve({
              importId: data.ImportId,
              isBatch: data.CampaignSplitBatchIndex > 0 && data.CampaignSplitBatchCount > 0,
            });
          }
        }
      }

      function checkStatus() {
        if (lastCallCompleted) {
          lastCallCompleted = false;
          this.odata.get(url)
            .then((rawData) => {
              let data = rawData;

              if (_.isFunction(dataMapper)) {
                data = dataMapper(rawData);
              }
              onStatusCheckSuccess.call(this, data);
            })
            .catch((err) => {
              failedAttempts += 1;
              if (failedAttempts === MAX_FAILED_ATTEMPTS) {
                clearInterval(this.timeOut);
                reject(err);
              }
            })
            .finally(() => {
              lastCallCompleted = true;
            });
        }
      }

      this.timeOut = setInterval(checkStatus.bind(this), 1000);
    });
  }

  stopPollingStatus() {
    clearInterval(this.timeOut);
  }

  exportErrorFile(importId) {
    const url = this.formatUrl({
      url: exportErrorFileUrlTmpl,
      queryParameters: {
        importId,
      },
    });

    return this.odata.get(url);
  }

  exportErrorSummaryFile(blobName) {
    const url = getBulkFileUrlTmpl.replace('{{blobName}}', blobName)
      .replace('{{bulkFileType}}', importErrorBulkFileType);

    return this.odata.get(url);
  }

  setInProcessImportCookie({ importType, importId }) {
    const inProcessImportSession = {
      ImportType: importType,
      AccountId: this.aid,
      CustomerId: this.cid,
      ImportId: importId,
    };


    this.addSubCookieToImportCookie({
      InProcessImport: inProcessImportSession,
    }, false);
  }

  getImportCookieAsJson() {
    const cookieText = !_.isEmpty(this.getImportCookieFromLocal()) ?
      this.getImportCookieFromLocal() : get(IMPORT_COOKIE_NAME, false);
    const parsedCookie = parseSync(cookieText);

    if (!parsedCookie) {
      this.logTrace(`Failed to parse import cookie. Raw cookie text: ${cookieText}`);
    }

    return parsedCookie;
  }

  setImportCookie(cookieJson) {
    this.logTrace(`Setting import cookie: ${JSON.stringify(cookieJson)}`);
    let setCookieArgs = [
      IMPORT_COOKIE_NAME, stringifySync(cookieJson),
      false, COOKIE_PATH, this.sharedCookieDomain,
    ];
    // samesite=none is not available for non secure protocol (https:)
    if (window.location.protocol === 'https:') {
      setCookieArgs = setCookieArgs.concat([
        true, 'none',
      ]);
    }
    set.call(undefined, ...setCookieArgs);
    this.setImportCookieToLocal(stringifySync(cookieJson));
  }

  getValueInSubImportCookieByAccountId(...keysInSubCookie) {
    const cookieJson = this.getImportCookieAsJson() || {};
    const subCookieValue = parseSync(cookieJson[this.cookieKeyByAccountId()]);

    if (!subCookieValue) {
      this.logTrace(`No subCookie found for ${this.cookieKeyByAccountId()}. Cookie value: ${stringifySync(cookieJson)}`);
      return [];
    }
    const result = [];

    _.each(keysInSubCookie, (keyInSubCookie) => {
      const val = subCookieValue[keyInSubCookie];

      result.push(val);
    });

    if (_.size(result) === 0) {
      this.logTrace(`No value found for keys ${keysInSubCookie}. Cookie value: ${stringifySync(cookieJson)}`);
    }

    return result;
  }

  /**
   * Adds new sub-cookies to the import cookie, overwritting any existing sub-cookies
   * stored with the same key and (optionally) account id.
   * @param { Object } subCookiesToAdd - object containing sub cookies to add to import cookie,
   * defined where each property name is the sub-cookie's key, while each property value
   * is the sub-cookie value.
   * @param { boolean } serializeByAccountId - boolean indicating whether or not to seralize
   * the subCookiesToAdd by account id
   * @returns { void }
   */
  addSubCookieToImportCookie(subCookiesToAdd, serializeByAccountId = true) {
    const cookieJson = this.getImportCookieAsJson() || {};
    const subCookieKeys = _.keys(subCookiesToAdd);

    if (serializeByAccountId) {
      let currentVal = {};

      if (cookieJson[this.cookieKeyByAccountId()]) {
        currentVal = parseSync(cookieJson[this.cookieKeyByAccountId()]) || {};
      }

      _.each(subCookieKeys, (key) => {
        currentVal[key] = subCookiesToAdd[key];
      });
      cookieJson[this.cookieKeyByAccountId()] = stringifySync(currentVal);
    } else {
      _.each(subCookieKeys, (key) => {
        cookieJson[key] = subCookiesToAdd[key];
      });
    }

    this.setImportCookie(cookieJson);
  }

  removeImportCookieValue(...keysInSubCookie) {
    let cookieJson = this.getImportCookieAsJson() || {};
    let subCookieValue = parseSync(cookieJson[this.cookieKeyByAccountId()]);

    if (!subCookieValue) {
      return;
    }
    let isModified = false;

    _.each(keysInSubCookie, (keyInSubCookie) => {
      if (subCookieValue[keyInSubCookie]) {
        subCookieValue = _.omit(subCookieValue, keyInSubCookie);
        isModified = true;
      }
    });

    if (isModified) {
      if (_.isEmpty(subCookieValue)) {
        cookieJson = _.omit(cookieJson, this.cookieKeyByAccountId());
      } else {
        cookieJson[this.cookieKeyByAccountId()] = stringifySync(subCookieValue);
      }
      this.setImportCookie(cookieJson);
    }
  }

  adjustImportUserPreference(preference) {
    if (preference) {
      preference.AdjustmentForBids = preference.AdjustmentForBids || { EnableAdjustment: false, AdjustmentValue: null };
      preference.AdjustmentForCampaignBudgets = preference.AdjustmentForCampaignBudgets || { EnableAdjustment: false, AdjustmentValue: null };

      const defaultPrefixAndSuffix = {
        EnablePrefixAndSuffix: false, PrefixString: null, SuffixString: null,
      };
      const defaultSearchAndReplace = {
        EnableSearchAndReplace: false, IgnoreCaseInPlainText: false, ReplaceString: null, SearchString: null, UseRegularExpression: false,
      };
      preference.PrefixAndSuffixForUrls = preference.PrefixAndSuffixForUrls || { ...defaultPrefixAndSuffix };
      preference.SearchAndReplaceForUrls = preference.SearchAndReplaceForUrls || { ...defaultSearchAndReplace };
      preference.PrefixAndSuffixForTrackingTemplates = preference.PrefixAndSuffixForTrackingTemplates || { ...defaultPrefixAndSuffix };
      preference.SearchAndReplaceForTrackingTemplates = preference.SearchAndReplaceForTrackingTemplates || { ...defaultSearchAndReplace };
      preference.SearchAndReplaceForCustomParameters = preference.SearchAndReplaceForCustomParameters || { ...defaultSearchAndReplace };
      preference.SearchAndReplaceForFinalURLSuffix = preference.SearchAndReplaceForFinalURLSuffix || { ...defaultSearchAndReplace };
    }
    return preference;
  }

  getImportUserPreferences(importType) {
    const url = this.formatUrl({
      url: importUserPreferencesUrlTmpl,
      queryParameters: {
        importType,
      },
    });

    return this.odata.get(url);
  }

  getImportUserPreference(importType) {
    const url = this.formatUrl({
      url: importUserPreferenceUrlTmpl,
      queryParameters: {
        importType,
      },
    });

    return this.odata.get(url);
  }

  getDefaultImportUserPreference() {
    const url = this.formatUrl({
      url: defaultImportUserPreferenceUrlTmpl,
      queryParameters: {
        importType: this.importType,
      },
    });

    return this.odata.get(url);
  }

  getImportUserPreferencesWithBMCandMinInfo(importType) {
    const storeIds = _.map(
      _.pluck(this.storeList, 'Value'),
      id => parseInt(id, 10)
    );
    const preferences = _.extend(
      {},
      {
        AvailableStoreValues: storeIds,
        AvailableStoreValueMessages: _.pluck(this.storeList, 'Text'),
      }
    );
    return this.getImportUserPreference(importType)
      .then(userPref => (
        _.defaults(preferences, userPref, defaultOptions)
      ));
  }

  getMultiAccountImportUserPreference() {
    const url = this.formatUrl({
      url: importMultiAccountUserPreferenceUrlTmpl,
    });

    return this.odata.get(url);
  }

  getMultiAccountImportUserPreferenceWithBMCandMinInfo() {
    const storeIds = _.map(
      _.pluck(this.storeList, 'Value'),
      id => parseInt(id, 10)
    );
    const preferences = _.extend(
      {},
      {
        AvailableStoreValues: storeIds,
        AvailableStoreValueMessages: _.pluck(this.storeList, 'Text'),
      }
    );

    return this.getMultiAccountImportUserPreference()
      .then(userPref => (
        _.defaults(preferences, userPref, defaultOptions)
      ));
  }

  getLocalizedErrorMessage(code, isInImportProcess = false) {
    const genericErrorMsg = this.i18nErrors.getString('ErrorNumber_-1');
    const defaultImportFailedMsg = this.i18n.get('Import_FailedStatus');
    let msg;

    if (_.isString(code)) {
      msg = this.i18nErrors.getString(`Error_Campaign_API_${code}`);
    } else if (_.isNumber(code)) {
      msg = this.i18nErrors.getString(`ErrorNumber_${code}`);
    }

    return msg || (isInImportProcess ? defaultImportFailedMsg : genericErrorMsg);
  }

  logException(err) {
    if (!err || !_.isUndefined(err.readyState)) {
      // do not log for ajax failures since they are already logged by intrumentation.
      // and also this will make ajax whitelist not work.
      return;
    }

    let msg;

    if (err.status === 0) {
      msg = JSON.stringify(err);
      this.logTrace(msg);
      return;
    }

    if (err.message) {
      msg = err.message;
    }
    if (err.stack) {
      const st = `StackTrace: ${err.stack}`;

      if (msg) {
        msg += `\t${st}`;
      } else {
        msg = st;
      }
    }
    msg = msg || JSON.stringify(err);
    this.logError(msg);
  }

  getOpportunityDesc() {
    const {
      opportunityId = 'n/a',
      opportunityType = 'n/a',
      opportunitySource = 'n/a',
    } = this.deps || {};
    return JSON.stringify({
      opportunityType,
      opportunityId,
      opportunitySource,
    });
  }

  logApplyOpportunityInfoIfIfApplicable(taskItemId) {
    if (!taskItemId) return;

    if (this.opportunityApplyStatus === OpportunityApplyStatus.Opportunity_InProgress
      || this.opportunityApplyStatus === OpportunityApplyStatus.Opportunity_Succeed) {
      const info = _.extend({
        taskItemId,
        applyStatus: this.opportunityApplyStatus,
      }, _.pick(this.deps, 'opportunityId', 'opportunityType', 'opportunitySource'));
      this.logTrace(`ApplyOpportunityStatus: ${JSON.stringify(info)}`);
    }
  }

  logTrace = (msg, activity) => {
    const logActivity = activity || this.deps.defaultLogActivity;

    logActivity.trace(msg, logActivity.value);
  };

  logError = (msg, activity) => {
    const logActivity = activity || this.deps.defaultLogActivity;

    logActivity.error(msg, logActivity.value);
  };

  applyOpportunityIfApplicable({ scheduleOption } = {}) {
    // if there is no checkOpportunityType function, then do nothing.
    // Or if check type failed, do nothing.
    if (!this.checkOpportunityType || !this.checkOpportunityType()) {
      return;
    }

    // if opportunity is already applied.
    if (this.opportunityApplyStatus === OpportunityApplyStatus.Opportunity_InProgress
      || this.opportunityApplyStatus === OpportunityApplyStatus.Opportunity_Succeed) {
      this.logTrace(`Can't apply the opportunity because it started/finished applied. Status is ${this.opportunityApplyStatus}`);
      return;
    }

    if (!this.cid || !this.aid) {
      this.logTrace(`no cid/aid when applying opportunity, cid: ${this.cid || 'n/a'}, aid: ${this.aid || 'n/a'}`);
      return;
    }

    const { opportunityId, opportunitySource } = this.deps;
    let { opportunityType } = this.deps;
    if (opportunityType && opportunityId) {
      this.logTrace(`Try to apply opportunity: ${this.getOpportunityDesc()}`);
      // Old link from UCM G! import tactic will be reimport and first.
      if (opportunityType === 'reimport') {
        opportunityType = 'GoogleImportScheduledOpportunity';
      } else if (opportunityType === 'first') {
        opportunityType = 'GoogleImportOpportunity';
      }

      // if the type is reimport, runnow and once job can't be qualified.
      if (opportunityType.toLocaleLowerCase().indexOf('scheduled') >= 0) {
        if (!scheduleOption || scheduleOption === 'Once' || scheduleOption === 'Now') {
          this.logTrace(`Won't apply the opportunity when scheduled to ${scheduleOption} for opportunityType ${opportunityType}`);
          return;
        }
      }

      this.opportunityApplyStatus = OpportunityApplyStatus.Opportunity_InProgress;
      const url = `Customers(${this.cid})/Accounts(${this.aid})/Recommendation.ApplyRecBulk`;
      const odataTypePrefix = '#Microsoft.Advertiser.AdInsight.Opportunity.Core.Adoption.Interface.Entity';
      const Request = {
        '@odata.type': `${odataTypePrefix}.ApplyBulkAccountRecommendationRequest`,
        RecommendationType: opportunityType,
        UserAction: 'Accept',
        TrackingOnly: true,
        Channel: 'recommendationpage',
        UserInputs: [{
          '@odata.type': `${odataTypePrefix}.ApplyUserInput`,
          OpportunityId: opportunityId.toString(),
        }],
      };
      if (opportunitySource && opportunitySource.toLocaleLowerCase().indexOf('ucm') >= 0) {
        Request.Context = JSON.stringify({ Source: 'UCM' });
      }
      this.odata.post(url, {
        path: 'AdInsight/V2/',
        host: this.deps.config.get('adinsight.odata.url'),
        data: {
          Request,
        },
      }).then((response) => {
        if (response.ErrorCode > 0) {
          this.opportunityApplyStatus = OpportunityApplyStatus.Opportunity_Failed;
          this.logError(`Failed to apply opportunity: ${this.getOpportunityDesc()} with response error result: ${JSON.stringify(response.Results && response.Results[0])}`);
        } else {
          this.opportunityApplyStatus = OpportunityApplyStatus.Opportunity_Succeed;
        }
      }).catch((err) => {
        this.opportunityApplyStatus = OpportunityApplyStatus.Opportunity_Failed;
        const { status, responseText } = err || {};
        this.logError(`Failed to apply opportunity: ${this.getOpportunityDesc()} with response:${status} error:${responseText}`);
      });
    }
  }

  getImportTaskItem(id) {
    return this.jsData.TaskItem.find(id, {
      adapter: 'odata',
      entitySet: this.isMultiAccountImport ?
        this.edm.currentCustomer.TaskItems : this.edm.currentAccount.TaskItems,
    }).then((taskItem) => {
      if (taskItem.ImportUserPreference) {
        // eslint-disable-next-line no-param-reassign
        taskItem.ImportUserPreference = this.adjustImportUserPreference(taskItem.ImportUserPreference);
      }
      return taskItem;
    });
  }

  pollGetTaskItemExecution(taskItemExecutionId, importStatusContainer) {
    const url = pollTaskExecutionUrlTmpl.replace('{{taskItemExecutionId}}', taskItemExecutionId);

    return this.pollSmartWorkflowImportStatus(
      url,
      importStatusContainer
    );
  }

  // update coupon API
  updateFACCoupon(status, isCalloutardCoupon = false) {
    this.logTrace(`Try to update${isCalloutardCoupon ? ' calloutcard' : ''} FACCoupon status to : ${status}`);
    const couponToBeSet = isCalloutardCoupon ? this.calloutcardCoupon : this.coupon;

    if (couponToBeSet && this.ccJsData) {
      return Promise.resolve(this.ccJsData.FeatureAdoptionCoupon
        .update(
          couponToBeSet.id,
          {
            adoption: {
              ...couponToBeSet,
              Status: status,
            },
          }
        ))
        .tap((coupon) => {
          if (isCalloutardCoupon) {
            this.calloutcardCoupon = coupon;
          } else {
            this.coupon = coupon;
          }
        });
    }

    return Promise.reject(new Error('coupon or ccJsData not set'));
  }

  updateCouponStatus(taskItemPromise, status, isCalloutardCoupon) {
    const {
      showAlertHandler, isBillToCustomer, i18nAmdLoader, couponDetailsUrl, newI18n,
    } = this.deps;
    return taskItemPromise
      .tap(() => this.updateFACCoupon(status, isCalloutardCoupon)
        .then(() => {
          if (showAlertHandler) {
            let action = null;
            if (isBillToCustomer) {
              action = {
                message: i18nAmdLoader.getString('Notification_Action_ViewPromotionalOffers'),
                href: couponDetailsUrl,
              };
            }

            showAlertHandler({
              title: i18nAmdLoader.getString('FACAdoptionFinishSuccessMessage_Title'),
              message: isCalloutardCoupon ? newI18n.getString(_TL_("We'll notify you when the credit has been added to your account.")) : i18nAmdLoader.getString('FACAdoptionFinishSuccessMessage'),
              level: 'Confirmation',
              action,
            });
          }
        })
        .catch(() => {
          if (showAlertHandler) {
            showAlertHandler({
              message: i18nAmdLoader.getString('GenericFACAdoptionFinishErrorMessage'),
              level: 'Error',
            });
          }
        }));
  }
}
